import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { MutationConfig } from '@/lib/tanstack-query';
import { PayoutMethod } from '@/types/api/payout-account';
import { MobileNumberSchema } from '@/validators/shared/utility/mobile-number.schema';
import { listPayoutAccountsQueryOptions } from './list-payout-accounts';

const CreateSwishPayoutAccountInputSchema = z.object({
  method: z.literal(PayoutMethod.Swish),
  swishNumber: MobileNumberSchema,
});

const CreateBankTransferPayoutAccountInputSchema = z.object({
  method: z.literal(PayoutMethod.BankTransfer),
  selectionId: z.string().nonempty('Inget konto valt'),
});

export const CreatePayoutAccountInputSchema = z.discriminatedUnion('method', [
  CreateSwishPayoutAccountInputSchema,
  CreateBankTransferPayoutAccountInputSchema,
]);

export type CreatePayoutAccountInput = z.infer<typeof CreatePayoutAccountInputSchema>;

export type CreatePayoutAccountResult = {
  orgId: string;
};

export const createPayoutAccount = async ({
  data,
}: {
  data: CreatePayoutAccountInput;
}): Promise<CreatePayoutAccountResult> => {
  const payoutMethodData: any = {};

  if (data.method === PayoutMethod.Swish) payoutMethodData.swishNumber = data.swishNumber;
  if (data.method === PayoutMethod.BankTransfer) payoutMethodData.selectionId = data.selectionId;

  return (
    await api.post(`/payments/payout-accounts`, {
      payoutMethod: data.method,
      payoutMethodData,
    })
  ).data;
};

type UseCreatePayoutAccountOptions = {
  mutationConfig?: MutationConfig<typeof createPayoutAccount>;
};

export const useCreatePayoutAccount = ({ mutationConfig }: UseCreatePayoutAccountOptions = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: listPayoutAccountsQueryOptions().queryKey });
      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: createPayoutAccount,
  });
};
